import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PublicNavbar from "@/components/layout/PublicNavbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { Wifi, Bath, Bed, Eye, Shield, BookOpen, MapPin, Sparkles, Users as UsersIcon } from "lucide-react";
import { motion } from "framer-motion";

const SINGLE_IMG = "https://static.prod-images.emergentagent.com/jobs/6d089f85-473b-4449-a6e3-5eb88daae93b/images/4e87d0130c90ae98ba68f87645f227d576e341858a9ef3bbbbba7ff831b686db.jpeg";
const categoryLabels = { single: "Single Room", shared: "Shared Room", premium: "Premium Suite", accessible: "Accessible Room" };
const statusColors = {
  available: "bg-emerald-100 text-emerald-700",
  reserved: "bg-amber-100 text-amber-700",
  occupied: "bg-sky-100 text-sky-700",
  maintenance: "bg-slate-100 text-slate-500",
};

export default function RoomsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(searchParams.get("category") || "all");
  const [floor, setFloor] = useState("all");

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== "all") params.category = category;
    if (floor !== "all") params.floor = floor;
    api.get("/rooms", { params }).then((r) => setRooms(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [category, floor]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="rooms-page">
      <PublicNavbar />
      <div className="pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <span className="text-sky-500 font-heading font-bold text-xs uppercase tracking-[0.25em]">Accommodation</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-black text-slate-900 tracking-tighter mt-1">Our Rooms</h1>
            <p className="text-slate-500 mt-2">Find the perfect space for your university journey.</p>
          </div>
          <div className="flex flex-wrap gap-4 mb-8" data-testid="room-filters">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-[180px] h-11 bg-white border-slate-200" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="single">Single Room</SelectItem>
                <SelectItem value="shared">Shared Room</SelectItem>
                <SelectItem value="premium">Premium Suite</SelectItem>
                <SelectItem value="accessible">Accessible Room</SelectItem>
              </SelectContent>
            </Select>
            <Select value={floor} onValueChange={setFloor}>
              <SelectTrigger className="w-[180px] h-11 bg-white border-slate-200" data-testid="filter-floor"><SelectValue placeholder="Floor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Floors</SelectItem>
                {[1,2,3,4,5].map(f => <SelectItem key={f} value={String(f)}>Floor {f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="bg-white rounded-lg border border-slate-200 overflow-hidden animate-pulse">
                  <div className="h-48 bg-slate-100" /><div className="p-5 space-y-3"><div className="h-4 bg-slate-100 rounded w-2/3" /><div className="h-3 bg-slate-100 rounded w-1/2" /></div>
                </div>
              ))}
            </div>
          ) : rooms.length === 0 ? (
            <div className="text-center py-20"><p className="text-slate-500 text-lg">No rooms found matching your criteria.</p></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {rooms.map((room, i) => (
                <motion.div key={room._id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                  className="bg-white rounded-lg border border-slate-200 overflow-hidden hover:shadow-xl transition-shadow cursor-pointer group"
                  onClick={() => navigate(`/rooms/${room._id}`)} data-testid={`room-card-${room.number}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img src={room.images?.[0] || SINGLE_IMG} alt={`Room ${room.number}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <Badge className={`absolute top-3 right-3 ${statusColors[room.status]}`}>{room.status}</Badge>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-sky-600 uppercase tracking-wider">{categoryLabels[room.category] || room.category}</span>
                      <span className="text-xs text-slate-400">Floor {room.floor}</span>
                    </div>
                    <h3 className="font-heading text-lg font-bold text-slate-900 mb-1">Room {room.number}</h3>
                    <div className="flex items-center gap-3 text-xs text-slate-400 mb-3">
                      <span className="flex items-center gap-1"><Bed className="h-3.5 w-3.5" /> {room.capacity}</span>
                      <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5" /> Bath</span>
                      <span className="flex items-center gap-1"><Wifi className="h-3.5 w-3.5" /> WiFi</span>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <div><span className="font-mono text-xl font-bold text-slate-900">${room.price}</span><span className="text-sm text-slate-400">/mo</span></div>
                      <Button variant="ghost" size="sm" className="text-sky-600 hover:text-sky-700"><Eye className="h-4 w-4 mr-1" /> View</Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Why Legacy Heights */}
      <section className="bg-white py-20" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-amber-500 font-heading font-bold text-xs uppercase tracking-[0.25em]">Why Legacy Heights</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tighter mt-2">Everything you need</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: "24/7 Security", desc: "Round-the-clock security with CCTV and controlled access." },
              { icon: Wifi, title: "High-Speed WiFi", desc: "Reliable fiber-optic internet throughout the building." },
              { icon: BookOpen, title: "Study Spaces", desc: "Dedicated quiet zones and collaborative study rooms." },
              { icon: MapPin, title: "Prime Location", desc: "Walking distance to campus, shops, and transport." },
              { icon: Sparkles, title: "Modern Rooms", desc: "Fully furnished rooms with contemporary design." },
              { icon: UsersIcon, title: "Vibrant Community", desc: "Social events, common areas, and a diverse student family." },
            ].map((f) => (
              <div key={f.title} className="p-5 rounded-lg border border-slate-200 hover:border-sky-300 transition-colors group">
                <div className="h-10 w-10 rounded-lg bg-sky-100 flex items-center justify-center mb-3 group-hover:bg-sky-500 transition-colors">
                  <f.icon className="h-5 w-5 text-sky-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading font-bold text-slate-900 mb-1">{f.title}</h3>
                <p className="text-sm text-slate-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
